import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, styled, Typography, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";

import { useSetPasswordMutation } from "Auth/Api/Mutations/useSetPasswordMutation";
import { BlFormInput } from "Forms/Components/BlFormInput";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { ApiCallError } from "Infrastructure/Api/ApiUtils";
import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Spacing } from "Infrastructure/Themes/Themes";
import {
  getTranslation,
  Resources,
  useResource,
} from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { BlButton } from "Shared/Buttons/BlButton";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";

const SetPasswordSchema = z
  .object({
    password: z.string().min(8, {
      error: () => getTranslation(Resources.SetPassword.Error.WeakPassword),
    }),
    confirmPassword: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    error: () => getTranslation(Resources.SetPassword.PasswordsMustMatch),
    path: ["confirmPassword"],
  });

type SetPasswordFormData = z.infer<typeof SetPasswordSchema>;

const Form = styled("form")`
  width: 100%;
  max-width: 400px;
`;

export const SetPasswordForm = (): React.ReactNode => {
  const theme = useTheme();
  const { t } = useResource();
  const navigate = useAppNavigate();
  const { token } = useParams<{ token: string }>();
  const { mutate, error } = useSetPasswordMutation();

  const form = useForm<SetPasswordFormData>({
    resolver: zodResolver(SetPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  useHandleServerError({
    form,
    error,
    formKey: "password",
    resource: Resources.SetPassword.Error,
  });

  const submit = (data: SetPasswordFormData) => {
    if (!token) return;
    mutate(
      { token, password: data.password },
      {
        onSuccess: () => {
          toast.success(t(Resources.SetPassword.SuccessMessage));
          navigate(AppRouting.SignIn.path());
        },
      },
    );
  };

  if (
    error != null &&
    ApiCallError.isApiException(error) &&
    (error.data.status as number) === 404
  ) {
    return (
      <Box sx={{ width: "100%", maxWidth: "400px" }}>
        <Typography variant="h1" mb={Spacing.M}>
          {t(Resources.SetPassword.Error.TokenNotFound)}
        </Typography>
        <Typography
          variant="body1"
          color={theme.palette.text.secondary}
          mb={Spacing.L}
        >
          {t(Resources.SetPassword.Error.General)}
        </Typography>
        <UnstyledLink to={AppRouting.ResetPassword.path()} isUnderlined>
          <Typography variant="body2">
            {t(Resources.ResetPassword.Title)}
          </Typography>
        </UnstyledLink>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "400px" }}>
      <Typography variant="h1" mb={Spacing.S}>
        {t(Resources.SetPassword.Title)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={Spacing.L}>
        {t(Resources.SetPassword.Subtitle)}
      </Typography>
      <Form onSubmit={handleSubmit(submit)}>
        <Stack spacing={Spacing.L}>
          <Stack spacing={Spacing.S}>
            <BlFormInput
              control={control}
              name="password"
              errors={errors}
              type="password"
              autoComplete="new-password"
              label={t(Resources.SetPassword.Password.Label)}
              placeholder={t(Resources.SetPassword.Password.Label)}
            />
            <BlFormInput
              control={control}
              name="confirmPassword"
              errors={errors}
              type="password"
              autoComplete="new-password"
              label={t(Resources.SetPassword.ConfirmPassword.Label)}
              placeholder={t(Resources.SetPassword.ConfirmPassword.Label)}
            />
          </Stack>
          <BlButton type="submit" fullWidth color="primary">
            {t(Resources.SetPassword.Button)}
          </BlButton>
        </Stack>
      </Form>
    </Box>
  );
};
